import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * SINGLE SOURCE OF TRUTH: Filter options used by ALL charts
 * These date range filters match the DataTable implementation for consistency
 */
export const chartDateFilterOptions: SelectItem[] = [
	{ label: 'All', value: 'all' },
	{ label: 'Today', value: 'today' },
	{ label: 'Yesterday', value: 'yesterday' },
	{ label: 'Tomorrow', value: 'tomorrow' },
	{ label: 'This Week', value: 'this_week' },
	{ label: 'Last Week', value: 'last_week' },
	{ label: 'This Month', value: 'this_month' },
	{ label: 'Last Month', value: 'last_month' },
	{ label: 'Last 90 Days', value: 'last_90_days' },
	{ label: 'This Year', value: 'this_year' },
	{ label: 'Last Year', value: 'last_year' },
	{ label: 'Year to Date', value: 'year_to_date' },
	{ label: 'Custom', value: 'custom' },
];

/**
 * Default filter for all charts (Last 90 Days)
 */
export const DEFAULT_CHART_DATE_FILTER = chartDateFilterOptions[7]; // last_90_days

/**
 * Type-safe date range interface
 */
export interface DateRange {
	start: string; // YYYY-MM-DD format
	end: string;   // YYYY-MM-DD format
}

/**
 * Calculate date range from filter type
 * Extracted from DataTable to avoid duplication
 * Uses America/New_York timezone for consistency
 * 
 * @param filterType - The filter type value (e.g., 'today', 'this_week', 'year_to_date')
 * @returns DateRange object with start and end dates in YYYY-MM-DD format
 */
export function calculateChartDateRange(filterType: string): DateRange | null {
	const now = dayjs().tz('America/New_York');
	const format = 'YYYY-MM-DD';

	switch (filterType) {
		case 'today':
			return {
				start: now.startOf('day').format(format),
				end: now.endOf('day').format(format)
			};
		case 'yesterday':
			return {
				start: now.subtract(1, 'day').startOf('day').format(format),
				end: now.subtract(1, 'day').endOf('day').format(format)
			};
		case 'tomorrow':
			return {
				start: now.add(1, 'day').startOf('day').format(format),
				end: now.add(1, 'day').endOf('day').format(format)
			};
		case 'this_week':
			return {
				start: now.startOf('week').format(format),
				end: now.endOf('week').format(format)
			};
		case 'last_week':
			return {
				start: now.subtract(1, 'week').startOf('week').format(format),
				end: now.subtract(1, 'week').endOf('week').format(format)
			};
		case 'this_month':
			return {
				start: now.startOf('month').format(format),
				end: now.endOf('month').format(format)
			};
	case 'last_month':
		return {
			start: now.subtract(1, 'month').startOf('month').format(format),
			end: now.subtract(1, 'month').endOf('month').format(format)
		};
	case 'last_90_days':
		return {
			start: now.subtract(90, 'day').startOf('day').format(format),
			end: now.endOf('day').format(format)
		};
	case 'this_year':
			return {
				start: now.startOf('year').format(format),
				end: now.endOf('year').format(format)
			};
		case 'last_year':
			return {
				start: now.subtract(1, 'year').startOf('year').format(format),
				end: now.subtract(1, 'year').endOf('year').format(format)
			};
		case 'year_to_date':
			return {
				start: now.startOf('year').format(format),
				end: now.format(format)
			};
		default:
			return null;
	}
}

